import { useState, useCallback, useRef } from 'react';

export interface UseDragAndDropOptions {
  onDrop: (files: FileList) => void;
  disabled?: boolean;
}

export interface UseDragAndDropResult {
  isDragging: boolean;
  dragHandlers: {
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onDragEnter: (e: React.DragEvent) => void;
  };
  dropZoneRef: React.RefObject<HTMLDivElement>;
}

/**
 * Hook for handling drag and drop file uploads
 */
export function useDragAndDrop(options: UseDragAndDropOptions): UseDragAndDropResult {
  const { onDrop: handleDropCallback, disabled = false } = options;
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;

      e.preventDefault();
      e.stopPropagation();

      if (e.dataTransfer.types.includes('Files')) {
        setIsDragging(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;

      e.preventDefault();
      e.stopPropagation();

      dragCounter.current -= 1;

      // Only set dragging to false when we've left the drop zone completely
      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    },
    [disabled],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;

      e.preventDefault();
      e.stopPropagation();

      setIsDragging(false);
      dragCounter.current = 0;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleDropCallback(files);
      }
    },
    [handleDropCallback, disabled],
  );

  // Reset drag counter when entering drag zone
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      if (disabled) return;

      e.preventDefault();
      e.stopPropagation();

      dragCounter.current += 1;

      if (e.dataTransfer.types.includes('Files')) {
        setIsDragging(true);
      }
    },
    [disabled],
  );

  return {
    isDragging,
    dragHandlers: {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
      onDragEnter: handleDragEnter,
    },
    dropZoneRef,
  };
}
